import { Component, forwardRef, inject, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { TimezoneModel } from '../../core/models/data/timezone.model';
import { LocationService } from '../../core/services/location.service';
import { ServiceResultModel } from '../../core/models/service/service-result.model';
import { LocalizationConstants } from '../../core/constants/settings.constants';

@Component({
  selector: 'ms-timezone-dropdown',
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './timezone-dropdown.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimezoneDropdown),
      multi: true
    }
  ]
})
export class TimezoneDropdown implements OnInit, ControlValueAccessor {
  
  private destroyRef = inject(DestroyRef);
  private locationService = inject(LocationService);

  public selectedTimezone: TimezoneModel | undefined = undefined;
  public selectedTimezoneId = signal<string>('');
  public timezonesList = signal<TimezoneModel[] | undefined>(undefined);

  public isLoading: boolean = false;
  public disabled: boolean = false;

  public timezone = new FormControl('');

  private pendingValue: any = undefined;

  private onChange = (_value: any) => { };
  private onTouchedCallback = () => { };

  public ngOnInit(): void {
    this.timezone.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.onChange(value);
      });

    this.loadTimezones();
  }

  public async loadTimezones(): Promise<void> {
    this.isLoading = true;

    try {
      const response: ServiceResultModel<TimezoneModel[]> = await this.locationService.getTimezones();

      if (response?.data) {
        this.timezonesList.set(response.data);

        if (this.pendingValue !== undefined) {
          this.applyValue(this.pendingValue);
          this.pendingValue = undefined;
        } else if (!this.selectedTimezone && !this.selectedTimezoneId()) {
          this.setDefaultTimezone();
        }
      }
    } catch (error) {
      console.error('Error loading timezones:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private setDefaultTimezone(): void {
    const timezones = this.timezonesList();
    if (timezones && timezones.length > 0) {
      const defaultTimezoneId = LocalizationConstants.DEFAULT_TIMEZONE_ID();
      const defaultTimezone = timezones.find(tz => tz.id === defaultTimezoneId);
      if (defaultTimezone) {
        this.selectedTimezone = defaultTimezone;
        this.selectedTimezoneId.set(defaultTimezone.id);
        this.timezone.setValue(defaultTimezone.id, { emitEvent: false });
        this.onChange(defaultTimezone);
      }
    }
  }

  public onSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const timezoneId = target.value;
    const timezone = this.timezonesList()?.find(tz => tz.id === timezoneId);

    this.selectedTimezone = timezone;

    if (timezone) {
      this.selectedTimezoneId.set(timezone.id);
    }

    this.onChange(timezone);
  }

  public writeValue(value: any): void {
    const timezones = this.timezonesList();
    
    if (!timezones) {
      this.pendingValue = value;
      return;
    }

    this.applyValue(value);
  }

  private applyValue(value: any): void {
    if (typeof value === 'string') {
      this.selectedTimezoneId.set(value);
      this.timezone.setValue(value, { emitEvent: false });
      const timezones = this.timezonesList();
      if (timezones) {
        this.selectedTimezone = timezones.find(tz => tz.id === value);
      }
    } else if (value) {
      this.selectedTimezone = value;
      this.selectedTimezoneId.set(value?.id || '');
      this.timezone.setValue(value?.id || '', { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onBlur(): void {
    this.onTouchedCallback();
  }
}
